import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { EntitledDetailsInContextOfFiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedFocusArea } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import React, { useEffect, useMemo, useState } from 'react';
import { FilterMenuDualPanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterMenuDualPanelItemProps } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterListBoxItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { SearchableFilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/SearchableFilterListBox';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { isTimeSeriesTrendsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { getFocusAreasInSortedOrder } from 'libs/end-to-end/src/focus-area-utils';

export const MetricMenu = ({
  store,
  feature,
  databaseRevisionId,
  databaseId,
  organizationId,
  closeMenu,
  setPrimaryFilters,
  resetFiltersToInitialState,
  resetSecondaryFiltersToDefault,
  myObservation,
  focusArea,
  primaryFilterSelections,
  entitledFilterFeatureDetails,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  closeMenu: () => void;
  resetFiltersToInitialState: (
    selectionsToPreserve?: PrimaryFilterSelection[],
  ) => void;
  resetSecondaryFiltersToDefault: (newFocusArea: FocusArea) => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  primaryFilterSelections: PrimaryFilterSelection[];
  myObservation: MyObservation | null;
  focusArea: FocusArea | null;
  entitledFilterFeatureDetails: EntitledDetailsInContextOfFiltersFeatureName;
}) => {
  const language = useLanguage();

  const [selectedFocusArea, setSelectedFocusArea] = useState<string>('');
  const currentFocusArea = useMemo(
    () =>
      getSelectedFocusArea({
        feature,
        organizationId,
        databaseRevisionId,
        databaseId,
        store,
      }),
    [databaseId, databaseRevisionId, feature, organizationId, store],
  );

  // Select the first focusArea by default
  useEffect(() => {
    if (selectedFocusArea === '' && currentFocusArea !== null) {
      setSelectedFocusArea(currentFocusArea.focusArea);
    }
  }, [currentFocusArea, selectedFocusArea]);

  const sortedOrderForFocusAreas = getFocusAreasInSortedOrder({
    focusAreas: entitledFilterFeatureDetails.focusAreasWithMetrics,
  });

  const focusAreasOptions = useMemo(() => {
    if (focusArea) {
      return sortedOrderForFocusAreas.filter(
        (fA) => fA.focusArea === focusArea,
      );
    }
    return sortedOrderForFocusAreas;
  }, [focusArea, sortedOrderForFocusAreas]);

  const items = useMemo(() => {
    const allFocusAreas = focusAreasOptions.filter(
      (fa) => fa.metrics.length > 0,
    );
    const allItems: FilterMenuDualPanelItemProps[] = [];

    for (const focusArea of allFocusAreas) {
      let metrics = focusArea.metrics;

      // If metricTrends is enabled, filter based on its configuration
      if (
        feature === 'timeSeriesTrendsPrimary' &&
        isTimeSeriesTrendsEnabledForRevision({ revisionFlags: myObservation })
      ) {
        const myObservationMetrics = myObservation?.byMetricTrendsConfiguration
          ?.isEnabled
          ? myObservation.byMetricTrendsConfiguration.metrics
          : [];

        if (
          !focusArea.metrics.some((f) =>
            myObservationMetrics.some((m) => f.columnHeaderValue === m.metric),
          )
        ) {
          continue; // Skip focus areas that do not have any enabled metrics in trends configuration
        }

        metrics = metrics.filter((metric) =>
          myObservationMetrics.filter(
            (m) => m.metric === metric.columnHeaderValue && m.isEnabled,
          ),
        );
      }

      const metricItems: FilterListBoxItem[] = metrics.map((metric) => ({
        value: metric.columnHeaderValue,
        translatedContent: language.metricDisplayName({
          definition: metric,
        }),
        selected: currentFocusArea?.metric === metric.columnHeaderValue,
      }));

      allItems.push({
        key: focusArea.focusArea,
        translatedContent: language.focusAreaDisplayName({ focusArea }),
        onClick: () => {
          setSelectedFocusArea(focusArea.focusArea);
        },
        content: (
          <SearchableFilterListBox
            items={metricItems}
            multiple={false}
            onChange={(newItems) => {
              if (newItems.length <= 0) {
                return;
              }

              const newFocusArea: PrimaryFilterSelection = {
                primaryFilterKey: 'focusArea',
                value: [focusArea.focusArea, newItems[0]],
              };

              // if user selects a metric from a different focusArea we have to
              // reset all filters to initial state
              if (focusArea.focusArea !== currentFocusArea?.focusArea) {
                resetSecondaryFiltersToDefault(focusArea.focusArea);
                resetFiltersToInitialState([
                  newFocusArea,
                  ...primaryFilterSelections.filter(
                    (selection) => selection.primaryFilterKey !== 'focusArea',
                  ),
                ]);
              } else {
                setPrimaryFilters({
                  selections: [newFocusArea],
                });
              }

              closeMenu();
            }}
          />
        ),
        selected: selectedFocusArea === focusArea.focusArea,
        itemValue: focusArea.focusArea,
      });
    }
    return allItems;
  }, [
    focusAreasOptions,
    language,
    selectedFocusArea,
    currentFocusArea?.metric,
    currentFocusArea?.focusArea,
    closeMenu,
    primaryFilterSelections,
    resetSecondaryFiltersToDefault,
    resetFiltersToInitialState,
    setPrimaryFilters,
    feature,
    myObservation,
  ]);
  return <FilterMenuDualPanel items={items} bottomProps={'none'} />;
};
